<template>
  <div class="template-navbar">
    <!-- Mobile -->
    <div class="template-navbar-mobile">
      <portal-target name="template-menu-destination" />
      <div class="navbar-menu-toggler-wrapper">
        <div
          :id="`navbar-menu-toggler-${_uid}`"
          v-aha-emit-action
          name="template_library_category_dropdown_mobile"
          class="navbar-menu-toggler"
          @click="openMenu"
        >
          {{ $i18nPresenter.t('Categories') }}
          <aha-icon class="ml-3" name="polygon" />
        </div>
        <div
          v-if="activeCategoryLabel && !hideActiveCategory"
          class="active-category-label"
        >
          {{ activeCategoryLabel }}
        </div>
      </div>

      <portal
        v-if="isMobileMenuVisible"
        to="template-menu-destination"
      >
        <transition name="fade" appear>
          <div
            v-if="isMobileMenuVisible"
            v-click-outside="hideMenu"
            class="context-menu-category xl:hidden"
            :style="{
              top: `${TARGET_Y}px`,
              right: `${TARGET_X}px`,
              width: targetMenuWidth,
            }"
          >
            <div class="context-menu-category-wrapper">
              <ul class="vertical-tab-mobile">
                <li
                  v-for="item in tabs"
                  :key="item.id"
                  v-aha-emit-action
                  name="template_library_category_item_mobile"
                  class="vertical-tab-mobile-item"
                  @click="selectTab(item)"
                >
                  {{ $i18nPresenter.t(item.label) }}
                </li>
              </ul>
            </div>
          </div>
        </transition>
      </portal>
    </div>

    <!-- Desktop -->
    <div class="template-navbar-desktop">
      <div class="navbar-menu-wrapper">
        <vertical-tabs>
          <vertical-tab-item
            v-for="item in filteredTabs"
            :key="item.id"
            v-aha-emit-action
            name="template_library_category_item_desktop"
            class="vertical-tab-item"
            :item="item"
            :disabled="item.disabled"
            :isActive="activeTab === item.id && !hideActiveCategory"
            :noActiveStateOnMobile="true"
            :translateLabel="true"
            @select-tab="selectTab(item)"
          />
        </vertical-tabs>
        <div
          v-if="isShowExpand"
          v-aha-emit-action
          name="template_library_expand_category"
          class="expand"
          @click="expandFullMenu"
        >
          {{ $i18nPresenter.t('More categories…') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VerticalTabs from '@/components-v2/template-library/v2/VerticalTabs.vue'
import VerticalTabItem from '@/components-v2/template-library/v2/VerticalTabItem.vue'

export default {
  components: {
    VerticalTabs,
    VerticalTabItem,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    selectedCategoryLabel: {
      type: String,
      default: '',
    },
    hideActiveCategory: {
      type: Boolean,
      default: false,
    },
    activeTab: {
      type: [Number, null],
      default: null,
    },
  },
  data() {
    return {
      tabs: [],
      filteredTabs: [], // desktop only
      isMobileMenuVisible: false,
      TARGET_X: 0,
      TARGET_Y: 60,
      targetMenuWidth: 'auto',
    }
  },
  computed: {
    activeCategoryLabel() {
      if (this.hideActiveCategory) return null

      if (this.selectedCategoryLabel) return this.$i18nPresenter.t(this.selectedCategoryLabel)

      if (!this.categories || !this.categories.length) return this.$i18nPresenter.t('Discover')

      const category = this.categories.find((cat) => cat.id === this.activeTab)

      return category ? category.name : this.$i18nPresenter.t('Discover')
    },
    isShowExpand() {
      return this.filteredTabs && this.filteredTabs.length < this.tabs.length
    },
  },
  watch: {
    categories: {
      handler(value) {
        if (!this.isValidCategories(value)) return

        this.tabs = this.createTabs(value)
        this.filteredTabs = this.filterTabs(this.tabs, this.activeTab)
      },
      immediate: true,
    },
    activeTab: {
      handler(value) {
        if (this.shouldExpandMenu(value)) {
          this.expandFullMenu()
        }
      },
      immediate: true,
    },
  },
  methods: {
    isValidCategories(value) {
      return value && value.length
    },
    createTabs(categories) {
      return [
        {
          id: null,
          label: 'Discover',
        },
        ...categories.map((category) => ({
          id: category.id,
          label: category.name,
        })),
      ]
    },
    filterTabs(tabs, activeTab) {
      if (tabs.length > 8 && tabs.findIndex((tab) => tab.id === activeTab) < 8) {
        return tabs.slice(0, 8)
      }

      return tabs
    },
    selectTab(item) {
      this.hideMenu()

      if (item.id === this.activeTab && item.id !== null) return

      this.$emit('onChange', item)
    },
    openMenu() {
      const targetEl = document.querySelector(`#navbar-menu-toggler-${this._uid}`)
      const windowWidth = document.documentElement.clientWidth // Exclude scrollbar from the calculation

      if (!targetEl) return

      const rect = targetEl.getBoundingClientRect()

      if (windowWidth < 768) {
        this.targetMenuWidth = `${rect.width}px` // Mobile: Set menu width to match the button width
      } else {
        this.targetMenuWidth = 'auto'
      }

      this.isMobileMenuVisible = true
    },
    hideMenu() {
      this.isMobileMenuVisible = false
    },
    expandFullMenu() {
      this.filteredTabs = this.tabs
    },
    shouldExpandMenu(value) {
      if (!this.tabs || !this.tabs.length) return false

      const isInTabs = this.tabs.some((tab) => tab.id === value)
      const isNotInFilteredTabs = !this.filteredTabs.some((tab) => tab.id === value)

      return isInTabs && isNotInFilteredTabs
    },
  },
}
</script>

<style lang="scss" scoped>
.template-navbar-mobile {
  position: relative;
  display: block;
}

.vertical-tab-mobile {
  font-weight: 400;
  cursor: pointer;
  transition: all 0.1s ease;
  text-transform: capitalize;
}

.navbar-menu-toggler-wrapper {
  @apply flex flex-col justify-between items-center;
  @apply md:mt-0 md:flex md:flex-row-reverse;

  .active-category-label {
    @apply md:mt-0;
  }
}

.active-category-label {
  @apply font-bold text-base w-full mt-5;
}

.template-navbar-desktop {
  display: none;

  &::v-deep .vertical-tab-item {
    padding: 10px 12px 10px 16px;

    &.active::before {
      width: 4px;
    }
  }
}

.navbar-menu-wrapper {
  @apply bg-base-0 overflow-hidden w-full;
  @apply rounded-lg;
  box-shadow: 0px 0.94px 2.83px 0px #D3D7E1CF;
}

.navbar-menu-toggler {
  @apply border border-transparent transition-colors;
  padding: 12px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0px 1px 3px 0px #D3D7E1CF;
  background-color: #fff;
  cursor: pointer;
  width: 100%;
  @apply md:w-max md:flex-shrink-0 md:flex-grow-0;

  &:hover, &:focus {
    @apply border-primary-90;
  }
}

.expand {
  padding: 10px 12px 10px 16px;
  color: #348EF6;
  cursor: pointer;

  @apply hover:text-primary-110;
}

.context-menu-category {
  @apply absolute;
  z-index: 9999;

  .context-menu-category-wrapper {
    @apply px-4 py-3 relative;
    @apply rounded-md min-w-36 md:max-w-max;
    --tw-border-opacity: 1;
    border-color: rgba(235, 235, 235, var(--tw-border-opacity));
    --tw-shadow: 3px 3px 5px rgba(37, 42, 56, 0.1);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 transparent), var(--tw-ring-shadow, 0 0 transparent), var(--tw-shadow);
        --tw-bg-opacity: 1;
    background-color: rgba(255, 255, 255, var(--tw-bg-opacity));
  }

  .category-menu-header {
    @apply pb-3 mb-3 font-bold;
    border-bottom: 1px solid #D3D7E1;
  }

  .vertical-tab-mobile-item {
    @apply px-2 flex justify-start items-center cursor-pointer leading-9 hover:bg-base-10;
  }
}

@media (min-width: 1280px) {
  .template-navbar-mobile {
    display: none;
  }

  .template-navbar-desktop {
    display: block;
  }
}
</style>
